/**
 * GEA 浏览器配置适配器：仅 chain 必需，输出扫描服务和 WebSocket 字段。
 * 与 GC 浏览器共用核心，但保留 GEA 的 JSON 文件名、链字段和解密函数。
 * 生产不读取站点字典，普通源不足时启用 _99 指针兜底，失败由 App 提供手动重试。
 */
import axios from 'axios'
import { Decrypt } from './crypto'
import { createFrontUrlDiscovery, type FrontUrlStartOptions } from './frontUrlDiscovery'

type AnyObject = Record<string, string>

declare global {
  interface Window {
    Global_Front_Url: AnyObject
    Global_Front_Api: AnyObject
  }
}

const env = import.meta.env
const discovery = createFrontUrlDiscovery(
  {
    cacheScope: [
      'gea-new-explorer',
      env.VITE_NODE_ENV,
      env.VITE_FRONT_URL_ID,
      env.VITE_Front_API_ID,
    ].join(':'),
    isProduction: env.VITE_NODE_ENV === 'production',
    settingsUrl: `${env.VITE_FRONT_URL_SETTING}/frontUrlGea.json`,
    urlId: env.VITE_FRONT_URL_ID,
    apiId: env.VITE_Front_API_ID,
    // 普通源依次补齐链地址，仍缺必需服务时再尝试 _99 指针兜底。
    sources: [
      env.VITE_FRONT_URL_PROD_1,
      env.VITE_FRONT_URL_PROD_2,
      env.VITE_FRONT_URL_PROD_3,
      env.VITE_FRONT_URL_PROD_4,
    ],
    fallbackUrl: env.VITE_FRONT_URL_PROD_99,
    services: [
      {
        key: 'front_gea_api___gea_scan_api',
        jsonKey: 'chain',
        envUrl: env.VITE_BROWSER_BASE_URL,
        required: true,
        websocketKey: 'front_gea_api___gea_ws_api',
      },
    ],
  },
  {
    /**
     * 请求配置数据并返回 HTTP 响应体。
     * @param url 请求地址。
     * @param options 请求的取消信号、超时和响应类型配置。
     * @returns HTTP 响应体。
     */
    request: async (url, options) => (await axios.get(url, options)).data,
    // 解密函数属于项目适配，不能用其他项目的默认密钥替换。
    decrypt: Decrypt,
    /**
     * 将发现的 API 和可选站点配置发布到全局对象。
     * @param snapshot 已验证的配置快照，包含 api 和可选的 sites。
     * @returns 无返回值。
     */
    publish({ api, sites }) {
      window.Global_Front_Api = api
      if (sites) window.Global_Front_Url = sites
    },
  },
)

/**
 * 使所有在途发现任务失效，保留当前已发布的地址。
 * @returns 无返回值。
 */
export function invalidateFrontUrlRequests(): void {
  discovery.invalidate()
}

/**
 * 启动地址配置发现并发布有效配置。
 * @param data 非生产环境可选的配置数组、JSON 或 Base64 文本。
 * @returns 配置发布成功时为 true，失败或取消时为 false。
 */
export default function getFrontUrl(
  data?: FrontUrlStartOptions['developmentData'],
): Promise<boolean> {
  return discovery.start(data !== undefined ? { developmentData: data } : undefined)
}
